import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react'

import Joi from 'joi'
import styled from 'styled-components'
import countries from 'all-countries-and-cities-json'
import toast from 'react-hot-toast'

import { StandardInput } from '../../../components/Input'

import RadioButtonGroup from '../../../components/RadioButtonGroup'
import { SingleSelect, MultiSelect } from '../../../components/Select'
import RichTextbox from '../../../components/RichTextbox'
import Toggle from '../../../components/Toggle'

import { getFromLocalStorage } from '../../../utils/util'

import apiClient from '../../../helpers/apiClient'

const JobDetails = forwardRef((props, ref) => {
  const [jobTitle, setJobTitle] = useState('')
  const [engagementType, setEngagementType] = useState('full-time')
  const [activeVacancy, setActiveVacancy] = useState(0)
  const [vacancyPooling, setVacancyPooling] = useState(0)
  const [shift, setShift] = useState({})
  const [jobDesc, setJobDesc] = useState('')
  const [industry, setIndustry] = useState({})
  const [country, setCountry] = useState([])
  const [requiresCoaching, setRequiresCoaching] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})

  const [shiftList, setShiftList] = useState([])
  const [industryList, setIndustryList] = useState([])
  const [countryList, setCountryList] = useState([])

  const validationSchema = useMemo(() => {
    return Joi.object({
      jobTitle: Joi.string().label('Job Title').required(),
      activeVacancy: Joi.number().label('Active Vacancy').required(),
      shift: Joi.string().label('Shift').required(),
      jobDesc: Joi.string()
        .label('Job Description')
        .required()
        .custom((value, helpers) => {
          // Check if the value is empty or contains only an empty paragraph
          if (value.trim() === '<p><br></p>') {
            return helpers.error('any.invalid', {
              label: 'Job Description',
            })
          }
          return value
        })
        .messages({
          'any.required': '"Job Description" is required',
          'any.invalid': '"Job Description" is not allowed to be empty',
        }),
      industry: Joi.string().label('Industry').required(),
      country: Joi.array()
        .items(
          Joi.object({
            value: Joi.string().required(),
            label: Joi.string().required(),
          })
        )
        .min(1)
        .label('Countries')
        .required()
        .messages({
          'array.min': 'Please select at least one country.',
          'any.required': 'Countries selection is required.',
        }),
    })
  }, [])

  useEffect(() => {
    const jobDetails = getFromLocalStorage('jobDetails')

    setJobTitle(jobDetails?.jobTitle || '')
    setEngagementType(jobDetails?.engagementType || 'full-time')
    setActiveVacancy(jobDetails?.activeVacancy || 0)
    setVacancyPooling(jobDetails?.vacancyPooling || 0)
    setShift(jobDetails?.shift || '')
    setJobDesc(jobDetails?.jobDesc || '')
    setIndustry(jobDetails?.industry || '')
    setRequiresCoaching(jobDetails?.requiresCoaching || false)
    setCountry((jobDetails && JSON.parse(jobDetails?.country)) || [])

    apiClient
      .get('/industries')
      .then((resp) => {
        const result = resp?.data?.result?.map((e) => ({
          value: e.id,
          label: e.name,
        }))
        setIndustryList(result)
      })
      .catch((err) => console.log(err))

    setShiftList([
      { value: 'Morning Shift - PH Time', label: 'Morning Shift - PH Time' },
      {
        value: 'Afternoon Shift - PH Time',
        label: 'Afternoon Shift - PH Time',
      },
      {
        value: 'Night Shift - PH Time',
        label: 'Night Shift - PH Time',
      },
      {
        value: 'Rotational Shift - PH Time',
        label: 'Rotational Shift - PH Time',
      },
    ])

    const allCountries = Object.keys(countries).map((country) => ({
      value: country,
      label: country,
    }))

    setCountryList(allCountries)
  }, [])

  const handleSelectCountry = (e) => setCountry(e)
  const handleSelectShift = (e) => setShift(e)

  const handleNext = () => {
    const formData = {
      jobTitle,
      jobDesc,
      engagementType,
      activeVacancy,
      vacancyPooling,
      shift,
      industry,
      country: JSON.stringify(country),
      requiresCoaching,
    }

    const forValidation = {
      jobTitle,
      jobDesc,
      activeVacancy,
      shift: shift.value,
      industry: industry.label,
      country,
    }

    const result = validationSchema.validate(forValidation, {
      abortEarly: false,
    })

    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)

      toast.error('Please fix the validation errors to continue.')

      return false
    }

    localStorage.setItem('jobDetails', JSON.stringify(formData))

    return true
  }

  useImperativeHandle(ref, () => ({
    onNext: handleNext,
  }))

  return (
    <StyledJobDetails>
      <StandardInput
        title="Job Title"
        type="text"
        value={jobTitle}
        onChange={(value) => setJobTitle(value)}
        required
        wrapperClass={fieldErrors.jobTitle && 'error'}
        subText={fieldErrors.jobTitle}
      />

      <RadioButtonGroup
        title="Employment Type"
        list={[
          {
            value: 'full-time',
            label: 'Full-Time',
          },
          {
            value: 'part-time',
            label: 'Part-Time',
          },
        ]}
        selected={engagementType}
        onChange={setEngagementType}
      />

      <StandardInput
        type="number"
        title="Active Vacancy"
        value={activeVacancy}
        onChange={setActiveVacancy}
        required
        wrapperClass={fieldErrors.activeVacancy && 'error'}
        subText={fieldErrors.activeVacancy}
      />
      <StandardInput
        type="number"
        title="Vacancy for Pooling"
        value={vacancyPooling}
        onChange={setVacancyPooling}
      />

      <RichTextbox
        content={jobDesc}
        setContent={setJobDesc}
        title="Job Description"
        required
        marginBottom="1rem"
        className={'jobDesc' in fieldErrors && 'has-error'}
        errorMessage={'jobDesc' in fieldErrors && fieldErrors.jobDesc}
      />

      <SingleSelect
        title="Shift"
        options={shiftList}
        selected={shift}
        placeholder="Select Shift"
        onChange={handleSelectShift}
        wrapperClass={fieldErrors.shift && 'error'}
        subText={fieldErrors.shift}
      />

      <SingleSelect
        title="Industry"
        options={industryList}
        selected={industry}
        placeholder="Select Industry"
        onChange={setIndustry}
        wrapperClass={fieldErrors.industry && 'error'}
        subText={fieldErrors.industry}
      />

      <MultiSelect
        title="Countries"
        options={countryList}
        selected={country}
        placeholder="Select Country"
        onChange={handleSelectCountry}
        wrapperClass={fieldErrors.country && 'error'}
        subText={fieldErrors.country}
      />

      <Toggle
        toggle={requiresCoaching}
        setToggle={setRequiresCoaching}
        heading="This vacancy requires coaching"
      />
    </StyledJobDetails>
  )
})

const StyledJobDetails = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  width: 100%;
`
JobDetails.displayName = 'JobDetails'

export default JobDetails
